// =============================================================================
// LEAD QUALITY DASHBOARD - TYPE DEFINITIONS
// =============================================================================

export interface LeadQualityStats {
  total_calls: number
  paid_calls: number
  relevant_calls: number
  irrelevant_calls: number
  unclear_calls: number
  missed_calls: number
  booked_calls: number
  relevant_rate: number
  booking_rate: number
  irrelevant_rate: number
  funnel: FunnelData
  quality_breakdown: QualityBreakdownItem[]
  irrelevant_breakdown: IrrelevantReasonItem[]
}

export interface FunnelData {
  total_calls?: number
  paid_calls: number
  paid_calls_pct: number
  relevant_calls: number
  relevant_rate: number
  booked_calls: number
  booking_rate: number
}

export interface QualityBreakdownItem {
  label: string
  value: number
  percentage: number
  color: 'green' | 'red' | 'yellow' | 'gray'
}

export interface IrrelevantReasonItem {
  reason: string
  label: string
  count: number
  percentage: number
}

export interface CallRecord {
  id: string
  resource_id: string
  call_start_time: string | null
  date: string | null
  time: string | null
  duration_seconds: number | null
  duration_formatted: string
  customer_name: string | null
  customer_phone: string | null
  customer_type: 'new' | 'return'
  has_gclid: boolean
  gclid: string | null
  classification: 'relevant' | 'irrelevant' | 'unclear' | null
  classification_reason: string | null
  booked: boolean | null
  irrelevant_reason: string | null
  answered: boolean | null
  score: number | null
  appliance_type: string | null
  appliance_brand: string | null
  recording_url: string | null
}

export interface FilterOptions {
  date_filter: string
  from_date: string
  to_date: string
}

export interface DateFilterOption {
  value: string
  label: string
}

export interface PaginationInfo {
  page: number
  per_page: number
  total: number
  total_pages?: number
}

export interface LeadQualityData {
  stats: LeadQualityStats
  calls: CallRecord[]
  filters: FilterOptions
  date_filters: Record<string, string>
  pagination: PaginationInfo
}

// Classification badge styles
export const CLASSIFICATION_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  relevant: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Relevant' },
  irrelevant: { bg: 'bg-red-100', text: 'text-red-700', label: 'Irrelevant' },
  unclear: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Unclear' },
}

// Quality breakdown colors (for charts)
export const QUALITY_COLORS: Record<string, string> = {
  green: '#10b981',  // emerald-500
  red: '#ef4444',    // red-500
  yellow: '#f59e0b', // amber-500
  gray: '#6b7280',   // gray-500
}

// Irrelevant reason labels
export const IRRELEVANT_REASON_LABELS: Record<string, string> = {
  out_of_service_area: 'Out of Service Area',
  wrong_parts_inquiry: 'Wrong Parts / Inquiry',
  commercial_b2b: 'Commercial / B2B',
  vendor_sales: 'Vendor / Sales Call',
  existing_customer: 'Existing Customer',
  spam: 'Spam / Robocall',
  wrong_number: 'Wrong Number',
  unknown: 'Other',
}
